import type { SupabaseClient } from '../supabase/client';
import type {
  AdminCourierProof,
  Courier,
  CourierAssignmentErrorCode,
  CourierAssignmentResult,
  CourierErrorKey,
  CourierInput,
  CourierLinkActionResult,
  CourierPortalLinkActionInput,
  CourierPortalLinkActionResult,
  CourierProofSubmissionResult,
  CourierUnassignmentResult,
  CourierVerificationResult,
  CreateCourierVerificationInput,
  CourierUpdateInput,
  PublicCourierManifestResult,
  PublicCourierPortalResult,
  SubmitCourierDeliveryProofInput,
  SubmitCourierPortalDeliveryProofInput,
  VerifyCourierOtpInput,
} from '../types/courier';
import { validateCourierProofPhotoFile } from '../lib/courier-proof-photo-validation';

export interface AssignOrdersToCourierInput {
  readonly storeId: string;
  readonly courierId: string;
  readonly orderIds: readonly string[];
}

export interface UnassignOrdersFromCourierInput {
  readonly storeId: string;
  readonly orderIds: readonly string[];
}

interface RpcCourierAssignmentRow {
  readonly r_success: boolean;
  readonly r_error_code: string | null;
  readonly r_batch_id?: string | null;
}

function firstRow<T>(data: unknown, context: string): T {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error(`${context}: empty result`);
  return row as T;
}

function normalizeErrorCode(value: string | null | undefined): CourierAssignmentErrorCode | null {
  if (!value) return null;
  const allowed: readonly CourierAssignmentErrorCode[] = [
    'DELIVERY_FULFILLMENT_ONLY',
    'UNAUTHORIZED',
    'STORE_NOT_FOUND',
    'COURIER_NOT_FOUND',
    'COURIER_INACTIVE',
    'COURIER_NOT_VERIFIED',
    'EMPTY_ORDER_SET',
    'TOO_MANY_ORDERS',
    'ORDER_UNAVAILABLE',
    'INVALID_STATUS',
    'MANUAL_APPROVAL_REQUIRED',
    'ASSIGNMENT_CONFLICT',
    'ASSIGNMENT_NOT_FOUND',
    'ASSIGNMENT_HAS_PROOF',
    'LINK_NOT_FOUND',
    'UNKNOWN',
  ];
  return allowed.includes(value as CourierAssignmentErrorCode)
    ? (value as CourierAssignmentErrorCode)
    : 'UNKNOWN';
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function dedupeOrderIds(orderIds: readonly string[]): string[] {
  return [...new Set(orderIds.map((id) => id.trim()).filter(Boolean))];
}

async function invokeCourierLinkAction<T>(
  client: SupabaseClient,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await client.functions.invoke('courier-delivery-link-action', {
    body,
  });
  if (error) throw error;
  if (!data) throw new Error('courier-delivery-link-action: empty result');
  return data as T;
}

async function invokeCourierPortalLinkAction<T>(
  client: SupabaseClient,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await client.functions.invoke('courier-portal-link-action', {
    body,
  });
  if (error) throw error;
  if (!data) throw new Error('courier-portal-link-action: empty result');
  return data as T;
}

async function invokeCourierVerificationAction(
  client: SupabaseClient,
  body: Record<string, unknown>,
): Promise<CourierVerificationResult> {
  const { data, error } = await client.functions.invoke('courier-verification-action', { body });
  if (error) throw error;
  if (!data) throw new Error('courier-verification-action: empty result');
  return data as CourierVerificationResult;
}

export async function listCouriers(client: SupabaseClient, storeId: string): Promise<Courier[]> {
  const { data, error } = await client.rpc('list_store_couriers', {
    target_store_id: storeId,
  });
  if (error) throw error;
  return (data ?? []) as unknown as Courier[];
}

export async function createCourier(client: SupabaseClient, input: CourierInput): Promise<Courier> {
  const { data, error } = await client.rpc('create_store_courier', {
    target_store_id: input.storeId,
    courier_name: input.name,
    courier_phone_number: input.phoneNumber,
    courier_active: input.active,
    courier_internal_notes: input.internalNotes ?? undefined,
  });
  if (error) throw error;
  return firstRow<Courier>(data, 'create_store_courier');
}

export async function updateCourier(
  client: SupabaseClient,
  input: CourierUpdateInput,
): Promise<Courier> {
  const { data, error } = await client.rpc('update_store_courier', {
    target_store_id: input.storeId,
    target_courier_id: input.courierId,
    courier_name: input.name,
    courier_phone_number: input.phoneNumber,
    courier_active: input.active,
    courier_internal_notes: input.internalNotes ?? undefined,
  });
  if (error) throw error;
  return firstRow<Courier>(data, 'update_store_courier');
}

export async function softDeleteCourier(
  client: SupabaseClient,
  storeId: string,
  courierId: string,
): Promise<void> {
  const { error } = await client.rpc('soft_delete_store_courier', {
    target_store_id: storeId,
    target_courier_id: courierId,
  });
  if (error) throw error;
}

export async function assignOrdersToCourier(
  client: SupabaseClient,
  input: AssignOrdersToCourierInput,
): Promise<CourierAssignmentResult> {
  return invokeCourierLinkAction<CourierAssignmentResult>(client, {
    action: 'assign',
    storeId: input.storeId,
    courierId: input.courierId,
    orderIds: dedupeOrderIds(input.orderIds),
  });
}

export async function unassignOrdersFromCourier(
  client: SupabaseClient,
  input: UnassignOrdersFromCourierInput,
): Promise<CourierUnassignmentResult> {
  const { data, error } = await client.rpc('unassign_orders_from_courier', {
    target_store_id: input.storeId,
    target_order_ids: dedupeOrderIds(input.orderIds),
  });
  if (error) throw error;
  const row = firstRow<{
    readonly r_success: boolean;
    readonly r_error_code: string | null;
    readonly r_unassigned_order_ids: string[] | null;
    readonly r_revoked_batch_ids: string[] | null;
  }>(data, 'unassign_orders_from_courier');
  return {
    success: row.r_success,
    error_code: normalizeErrorCode(row.r_error_code),
    unassigned_order_ids: row.r_unassigned_order_ids ?? [],
    revoked_batch_ids: row.r_revoked_batch_ids ?? [],
  };
}

export async function createCourierVerification(
  client: SupabaseClient,
  input: CreateCourierVerificationInput,
): Promise<CourierVerificationResult> {
  return invokeCourierVerificationAction(client, {
    action: 'create',
    storeId: input.storeId,
    courierId: input.courierId,
  });
}

export async function resendCourierVerification(
  client: SupabaseClient,
  input: CreateCourierVerificationInput,
): Promise<CourierVerificationResult> {
  return invokeCourierVerificationAction(client, {
    action: 'resend',
    storeId: input.storeId,
    courierId: input.courierId,
  });
}

export async function verifyCourierOtp(
  client: SupabaseClient,
  input: VerifyCourierOtpInput,
): Promise<CourierVerificationResult> {
  return invokeCourierVerificationAction(client, {
    action: 'verify',
    storeId: input.storeId,
    courierId: input.courierId,
    otp: input.otp,
  });
}

export async function regenerateCourierDeliveryLink(
  client: SupabaseClient,
  storeId: string,
  batchId: string,
): Promise<CourierLinkActionResult> {
  return invokeCourierLinkAction<CourierLinkActionResult>(client, {
    action: 'regenerate',
    storeId,
    batchId,
  });
}

export async function resendCourierDeliveryLink(
  client: SupabaseClient,
  storeId: string,
  batchId: string,
): Promise<CourierLinkActionResult> {
  return invokeCourierLinkAction<CourierLinkActionResult>(client, {
    action: 'resend',
    storeId,
    batchId,
  });
}

export async function revokeCourierDeliveryLink(
  client: SupabaseClient,
  storeId: string,
  batchId: string,
): Promise<CourierLinkActionResult> {
  const { data, error } = await client.rpc('revoke_courier_delivery_link', {
    target_store_id: storeId,
    target_batch_id: batchId,
  });
  if (error) throw error;
  const row = firstRow<RpcCourierAssignmentRow>(data, 'revoke_courier_delivery_link');
  return {
    success: row.r_success,
    error_code: normalizeErrorCode(row.r_error_code),
    batch_id: row.r_batch_id ?? batchId,
    manifest_path: null,
    manifest_url: null,
    fragment_secret: null,
    expires_at: null,
    reused_existing: false,
  };
}

export async function getOrCreateCourierPortalLink(
  client: SupabaseClient,
  input: Omit<CourierPortalLinkActionInput, 'action'>,
): Promise<CourierPortalLinkActionResult> {
  return invokeCourierPortalLinkAction<CourierPortalLinkActionResult>(client, {
    action: 'get_or_create',
    storeId: input.storeId,
    courierId: input.courierId,
  });
}

export async function revokeCourierPortalLink(
  client: SupabaseClient,
  input: Omit<CourierPortalLinkActionInput, 'action'>,
): Promise<CourierPortalLinkActionResult> {
  return invokeCourierPortalLinkAction<CourierPortalLinkActionResult>(client, {
    action: 'revoke',
    storeId: input.storeId,
    courierId: input.courierId,
  });
}

export async function regenerateCourierPortalLink(
  client: SupabaseClient,
  input: Omit<CourierPortalLinkActionInput, 'action'>,
): Promise<CourierPortalLinkActionResult> {
  return invokeCourierPortalLinkAction<CourierPortalLinkActionResult>(client, {
    action: 'regenerate',
    storeId: input.storeId,
    courierId: input.courierId,
  });
}

export async function getPublicCourierManifest(
  client: SupabaseClient,
  batchId: string,
  rawSecret: string,
): Promise<PublicCourierManifestResult> {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(batchId) ||
    !rawSecret
  ) {
    return { status: 'invalid', manifest: null };
  }
  const { data, error } = await client.rpc('get_public_courier_manifest', {
    target_batch_id: batchId,
    raw_secret: rawSecret,
  });
  if (error) throw error;
  const result = data as PublicCourierManifestResult | null;
  if (result?.status === 'valid' || result?.status === 'invalid') return result;
  return { status: 'invalid', manifest: null };
}

export async function getPublicCourierPortal(
  client: SupabaseClient,
  portalLinkId: string,
  rawSecret: string,
): Promise<PublicCourierPortalResult> {
  if (!isUuid(portalLinkId) || !rawSecret) {
    return { status: 'invalid', portal: null };
  }
  const { data, error } = await client.functions.invoke('courier-portal-manifest', {
    body: { portalLinkId, rawSecret },
  });
  if (error) throw error;
  const result = data as PublicCourierPortalResult | null;
  if (
    result?.status === 'valid' ||
    result?.status === 'invalid' ||
    result?.status === 'rate_limited'
  ) {
    return result;
  }
  return { status: 'invalid', portal: null };
}

export async function submitCourierDeliveryProof(
  client: SupabaseClient,
  input: SubmitCourierDeliveryProofInput,
): Promise<CourierProofSubmissionResult> {
  if (!isUuid(input.batchId) || !isUuid(input.batchOrderId) || !isUuid(input.submissionId)) {
    return {
      success: false,
      error_code: 'INVALID_LINK',
      order_id: null,
      batch_order_id: null,
      proof: null,
      order_status: null,
      delivered_at: null,
      idempotent_replay: false,
    };
  }
  const validation = await validateCourierProofPhotoFile(input.photo);
  if (validation !== true) {
    return {
      success: false,
      error_code:
        validation === 'imageRequired'
          ? 'IMAGE_REQUIRED'
          : validation === 'imageTooLarge'
            ? 'IMAGE_TOO_LARGE'
            : 'IMAGE_INVALID_TYPE',
      order_id: null,
      batch_order_id: input.batchOrderId,
      proof: null,
      order_status: null,
      delivered_at: null,
      idempotent_replay: false,
    };
  }
  const formData = new FormData();
  formData.set('batch_id', input.batchId);
  formData.set('raw_secret', input.rawSecret);
  formData.set('batch_order_id', input.batchOrderId);
  formData.set('submission_id', input.submissionId);
  formData.set('photo', input.photo);
  const { data, error } = await client.functions.invoke('courier-proof-action', {
    body: formData,
  });
  if (error) throw error;
  if (!data) throw new Error('courier-proof-action: empty result');
  return data as CourierProofSubmissionResult;
}

export async function submitCourierPortalDeliveryProof(
  client: SupabaseClient,
  input: SubmitCourierPortalDeliveryProofInput,
): Promise<CourierProofSubmissionResult> {
  if (
    !isUuid(input.portalLinkId) ||
    !/^[0-9a-f]{64}$/.test(input.proofRef) ||
    !isUuid(input.submissionId)
  ) {
    return {
      success: false,
      error_code: 'INVALID_LINK',
      order_id: null,
      batch_order_id: null,
      proof: null,
      order_status: null,
      delivered_at: null,
      idempotent_replay: false,
    };
  }
  const validation = await validateCourierProofPhotoFile(input.photo);
  if (validation !== true) {
    return {
      success: false,
      error_code:
        validation === 'imageRequired'
          ? 'IMAGE_REQUIRED'
          : validation === 'imageTooLarge'
            ? 'IMAGE_TOO_LARGE'
            : 'IMAGE_INVALID_TYPE',
      order_id: null,
      batch_order_id: null,
      proof: null,
      order_status: null,
      delivered_at: null,
      idempotent_replay: false,
    };
  }
  const formData = new FormData();
  formData.set('portal_link_id', input.portalLinkId);
  formData.set('raw_secret', input.rawSecret);
  formData.set('proof_ref', input.proofRef);
  formData.set('submission_id', input.submissionId);
  formData.set('photo', input.photo);
  const { data, error } = await client.functions.invoke('courier-proof-action', {
    body: formData,
  });
  if (error) throw error;
  if (!data) throw new Error('courier-proof-action: empty result');
  return data as CourierProofSubmissionResult;
}

export async function getAdminCourierProof(
  client: SupabaseClient,
  storeId: string,
  proofId: string,
): Promise<AdminCourierProof> {
  const { data, error } = await client.functions.invoke('courier-proof-preview-action', {
    body: { storeId, proofId },
  });
  if (error) throw error;
  if (!data) throw new Error('courier-proof-preview-action: empty result');
  return data as AdminCourierProof;
}

export async function confirmCourierDeliveryProof(
  client: SupabaseClient,
  storeId: string,
  proofId: string,
): Promise<AdminCourierProof> {
  const rpcClient = client as unknown as {
    rpc(
      fn: 'confirm_courier_delivery_proof',
      args: { readonly target_store_id: string; readonly target_proof_id: string },
    ): Promise<{ readonly data: unknown; readonly error: unknown }>;
  };
  const { data, error } = await rpcClient.rpc('confirm_courier_delivery_proof', {
    target_store_id: storeId,
    target_proof_id: proofId,
  });
  if (error) throw error;
  if (!data) throw new Error('confirm_courier_delivery_proof: empty result');
  return data as AdminCourierProof;
}

export function mapCourierError(
  err: unknown,
  result?: { readonly error_code: string | null },
): CourierErrorKey {
  switch (result?.error_code) {
    case 'DELIVERY_FULFILLMENT_ONLY':
      return 'admin.orders.fulfillment.deliveryOnly';
    case 'INVALID_STATUS':
      return 'admin.orders.courierAssignment.errors.invalidStatus';
    case 'MANUAL_APPROVAL_REQUIRED':
      return 'admin.orders.courierAssignment.errors.manualApprovalRequired';
    case 'COURIER_INACTIVE':
      return 'admin.orders.courierAssignment.errors.inactiveCourier';
    case 'COURIER_NOT_VERIFIED':
      return 'admin.orders.courierAssignment.errors.unverifiedCourier';
    case 'ORDER_UNAVAILABLE':
      return 'admin.orders.courierAssignment.errors.ordersUnavailable';
    case 'TOO_MANY_ORDERS':
      return 'admin.orders.courierAssignment.errors.tooManyOrders';
    case 'LINK_NOT_FOUND':
      return 'admin.orders.courierAssignment.errors.linkExpired';
    case 'ASSIGNMENT_CONFLICT':
      return 'admin.orders.courierAssignment.errors.assignmentConflict';
    case 'ASSIGNMENT_NOT_FOUND':
      return 'admin.orders.courierAssignment.errors.assignmentNotFound';
    case 'ASSIGNMENT_HAS_PROOF':
      return 'admin.orders.courierAssignment.errors.assignmentHasProof';
    case 'UNAUTHORIZED':
      return 'admin.couriers.errors.permissionDenied';
    case 'OTP_INVALID':
      return 'admin.couriers.errors.otpInvalid';
    case 'OTP_EXPIRED':
    case 'OTP_NOT_FOUND':
      return 'admin.couriers.errors.otpExpired';
    case 'OTP_COOLDOWN':
      return 'admin.couriers.errors.otpCooldown';
    case 'OTP_TOO_MANY_ATTEMPTS':
      return 'admin.couriers.errors.tooManyAttempts';
    case 'ALREADY_VERIFIED':
      return 'admin.couriers.errors.alreadyVerified';
    case 'SMS_PROVIDER_UNAVAILABLE':
      return 'admin.couriers.errors.smsUnavailable';
    case 'INVALID_LINK':
      return 'admin.orders.courierProof.errors.invalidLink';
    case 'ALREADY_CONFIRMED':
      return 'admin.orders.courierProof.errors.alreadyConfirmed';
    case 'SIGNED_URL_FAILED':
      return 'admin.orders.courierProof.errors.signedUrlFailed';
    case 'IMAGE_INVALID_TYPE':
      return 'storefront.courierManifest.proof.errors.invalidType';
    case 'IMAGE_TOO_LARGE':
      return 'storefront.courierManifest.proof.errors.tooLarge';
    case 'UPLOAD_FAILED':
    case 'PROOF_MUTATION_FAILED':
      return 'storefront.courierManifest.proof.errors.uploadFailed';
    default:
      break;
  }

  if (err && typeof err === 'object') {
    const maybe = err as {
      readonly code?: string;
      readonly message?: string;
      readonly name?: string;
    };
    if (maybe.code === '23505') return 'admin.couriers.errors.phoneDuplicate';
    if (maybe.code === '42501') return 'admin.couriers.errors.permissionDenied';
    if (maybe.name === 'AuthRetryableFetchError') return 'admin.couriers.errors.networkFailed';
    if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
      return 'admin.couriers.errors.networkFailed';
    }
  }

  return 'admin.couriers.errors.networkFailed';
}
